import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Plus, Pencil, Trash2, Loader2, Search } from "lucide-react";
import {
  fetchCertificateTemplates,
  deleteCertificateTemplate,
} from "../../redux/slices/certificateTemplateSlice";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { useToast } from "../../components/ui/use-toast";

const TYPE_LABELS = {
  participation: "Participation",
  completion: "Completion",
  position_1: "1st Place",
  position_2: "2nd Place",
  position_3: "3rd Place",
};

function CertificateTemplates() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { templates, loading } = useSelector((s) => s.certificateTemplate);
  const [search, setSearch] = useState("");

  useEffect(() => {
    dispatch(fetchCertificateTemplates());
  }, [dispatch]);

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => {
      const name = (t.name || "").toLowerCase();
      const type = (TYPE_LABELS[t.type] || t.type || "").toLowerCase();
      return name.includes(q) || type.includes(q);
    });
  }, [templates, search]);

  const onDelete = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await dispatch(deleteCertificateTemplate(id)).unwrap();
      toast({ title: "Deleted", description: name });
    } catch (err) {
      toast({
        title: "Error",
        description: typeof err === "string" ? err : "Delete failed",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Certificate Templates</h1>
          <p className="text-muted-foreground">
            Design templates for participation, completion, and podium awards.
            PDFs are generated on demand — no files stored on the server.
          </p>
        </div>
        <Button asChild>
          <Link to="/certificate-templates/new">
            <Plus className="w-4 h-4 mr-2" />
            New template
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Templates</CardTitle>
          <CardDescription>
            Assign templates per tier in each course&apos;s certificate settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search certificate templates…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : templates.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No templates yet. Create one to get started.
            </p>
          ) : filteredTemplates.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No templates match “{search.trim()}”
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTemplates.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {TYPE_LABELS[t.type] || t.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {t.is_active ? (
                        <Badge>Active</Badge>
                      ) : (
                        <Badge variant="outline">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/certificate-templates/${t.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(t.id, t.name)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default CertificateTemplates;
